import React, { useState } from "react";
import * as Mui from "@mui/material";
import { makeStyles } from "tss-react/mui";
import * as Icons from "react-feather";
import { useAppThunkDispatch } from "redux/store";
import { Agent } from "model/agent";
import { updateAgent } from "redux/slice/agents";
import { PhotoUploadDialogButton } from "component/shared/photo-upload-dialog-button";
import { Upload } from "model/upload";
import { LabeledField } from "./labeled-field";

const useStyles = makeStyles()((theme) => ({
	paper: { maxWidth: 560, width: "100%", borderRadius: 8 },
	// Uniform 24px padding; body items stack with 16px gaps, then a 32px gap before the actions.
	content: {
		padding: 24,
		display: "flex",
		flexDirection: "column",
		gap: 32,
		"&:first-child": { paddingTop: 24 },
	},
	body: { display: "flex", flexDirection: "column", gap: theme.spacing(2), width: "100%" },
	title: { display: "flex", alignItems: "center", justifyContent: "space-between" },
	titleText: { fontSize: 24, lineHeight: "32px", fontWeight: 700, color: theme.palette.custom.textPrimary },
	// Divider stays inside the padded body (not bleeding to the modal edges).
	divider: { width: "100%" },
	// 16px rhythm between the radios and the indented field, matching the body gap.
	radioGroup: { display: "flex", flexDirection: "column", gap: theme.spacing(2) },
	radioOption: { marginLeft: 0, marginBottom: 0 },
	// URL field indented under the radio and capped at the design's 340px (40px indent + 340).
	urlField: { paddingLeft: 40, width: 380, maxWidth: "100%", boxSizing: "border-box" },
	uploadRow: { paddingLeft: 40 },
	actions: { display: "flex", justifyContent: "flex-end", gap: 8 },
	button: { textTransform: "none", fontWeight: 600, borderRadius: 8 },
}));

interface Props {
	open: boolean;
	agent: Agent;
	onClose: () => void;
}

/**
 * Profile Photo modal for the refreshed detail view (Figma 1242:21488). Two sources: "Link to
 * image" (URL field) and "Upload image" (Cloudinary), saved via updateAgent's photoUrl. Distinct
 * from the legacy shared Photo dialog so its copy/label-above styling can match the design.
 */
export const PhotoModal: React.FC<Props> = ({ open, agent, onClose }) => {
	const { classes } = useStyles();
	const dispatch = useAppThunkDispatch();

	const [source, setSource] = useState<"url" | "upload">(agent.photoUrl ? "url" : "upload");
	const [photoUrl, setPhotoUrl] = useState(agent.photoUrl || "");
	const [saving, setSaving] = useState(false);

	const handleUpload = (uploads: Upload[]) => {
		const upload = uploads.find((u) => u.url);
		if (upload) {
			setSource("url");
			setPhotoUrl(upload.url);
		}
	};

	const handleSave = async () => {
		setSaving(true);
		await dispatch(updateAgent({ agent: { ...agent, photoUrl } }));
		setSaving(false);
		onClose();
	};

	return (
		<Mui.Dialog open={open} onClose={onClose} classes={{ paper: classes.paper }}>
			<Mui.DialogContent className={classes.content}>
				<div className={classes.body}>
					<div className={classes.title}>
						<Mui.Typography className={classes.titleText}>Profile Photo</Mui.Typography>
						<Mui.IconButton size="small" onClick={onClose}><Icons.X size={24} /></Mui.IconButton>
					</div>
					<Mui.Divider className={classes.divider} />
					<Mui.RadioGroup className={classes.radioGroup} value={source} onChange={(e) => setSource(e.target.value as "url" | "upload")}>
						<Mui.FormControlLabel
							className={classes.radioOption}
							value="url"
							control={<Mui.Radio color="secondary" />}
							label="Link to image"
						/>
						{source === "url" && (
							<div className={classes.urlField}>
								<LabeledField
									label="URL"
									value={photoUrl}
									onChange={setPhotoUrl}
									placeholder="www.image.com"
								/>
							</div>
						)}
						<Mui.FormControlLabel
							className={classes.radioOption}
							value="upload"
							control={<Mui.Radio color="secondary" />}
							label="Upload image"
						/>
						{source === "upload" && (
							<div className={classes.uploadRow}>
								<PhotoUploadDialogButton
									callback={handleUpload}
									resourceType="image"
									maxFiles={1}
									label="Upload Image"
									icon={<Icons.Upload size={16} />}
									variant="outlined"
									color="primary"
								/>
							</div>
						)}
					</Mui.RadioGroup>
				</div>
				<div className={classes.actions}>
					<Mui.Button className={classes.button} variant="outlined" color="primary" onClick={onClose}>Cancel</Mui.Button>
					<Mui.Button className={classes.button} variant="contained" color="primary" disabled={saving} onClick={handleSave}>Save</Mui.Button>
				</div>
			</Mui.DialogContent>
		</Mui.Dialog>
	);
};
